import type { MouseEvent, FC } from 'react'
import { Fragment } from 'react'
import { useTranslation } from 'react-i18next'

import { Body1, Box, Link, Stack } from '@dhl-ui/components'
import { useCookiePreferenceButton } from '@dhl/onetrust'

import { type Links } from './model.js'

type Props = {
  /**
   * Whether the content should be displayed in a single line.
   * Set to true to display links or content in a condensed one-liner format.
   */
  singleLine?: boolean

  /**
   * A list of visible links that should be shown in the UI.
   * Can include 'company', 'termsAndConditions', and 'privacy'.
   */
  visibleLinks?: Links[]
}

type LegalLinkProps = {
  link: Links
}

export const LegalLink: FC<LegalLinkProps> = ({ link }) => {
  const { t } = useTranslation()

  return (
    <Link
      external
      href={t(`legal.${link}.labelLink`)}
      rel="noreferrer"
      target="_blank"
      title={t(`legal.${link}.labelText`)}
    >
      {t(`legal.${link}.labelText`)}
    </Link>
  )
}

export const LegalLinks: FC<Props> = ({ singleLine, visibleLinks = ['company', 'privacy'] }) => {
  const { t } = useTranslation()
  const { cookiePreferenceId, onCookiePreferenceButtonClick, cookiePreferenceText, showCookiePreferenceButton } =
    useCookiePreferenceButton()

  const handleCookiePreferenceButtonClick = (e: MouseEvent) => onCookiePreferenceButtonClick(e)

  const showVisibleLinks = visibleLinks.length > 0

  return (
    <Stack sx={{ display: singleLine ? 'inline-flex' : 'flex', alignItems: 'center' }}>
      {showVisibleLinks && (
        <Stack
          gap={1}
          sx={{
            alignItems: 'center',
            flexWrap: 'wrap',
            flexDirection: singleLine ? 'row' : 'column',
            justifyContent: 'center',
          }}
        >
          {visibleLinks.map((link, index) => (
            <Fragment key={link}>
              <LegalLink link={link} />
              {singleLine && index < visibleLinks.length - 2 && <Body1 sx={{ mr: 0.5 }}>,</Body1>}
              {singleLine && visibleLinks.length - (index + 1) === 1 && <Body1>{t('legal.andText')}</Body1>}
            </Fragment>
          ))}
        </Stack>
      )}

      {showCookiePreferenceButton && (
        <Box sx={{ mt: showVisibleLinks ? 1 : 0, textAlign: 'center' }}>
          <Link
            id={cookiePreferenceId}
            href="#cookie"
            title={cookiePreferenceText}
            onClick={handleCookiePreferenceButtonClick}
          >
            {cookiePreferenceText}
          </Link>
        </Box>
      )}
    </Stack>
  )
}
